import { useGoogleLogin } from "@react-oauth/google";
import * as Sentry from "@sentry/react";
import React, { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Button } from "../../components/CTA";
import { Card } from "../../components/Card";
import { CardDivider } from "../../components/Divider";
import { Flex } from "../../components/Flex";
import { H1 } from "../../components/Heading";
import { Input } from "../../components/Input";
import Loading from "../../components/Loading";
import { Logo } from "../../components/Logo";
import { Page } from "../../components/Page";
import { Text } from "../../components/Text";
import { ExternalTextLink, TextLink } from "../../components/TextLink";
import { View } from "../../components/View";
import { CheckCircle } from "../../components/icons/CheckCircle";
import { FacebookIconCircle } from "../../components/icons/FacebookIconCircle";
import { GoogleIcon } from "../../components/icons/GoogleIcon";
import config, { MOBILE_BREAKPOINT } from "../../config";
import {
  AuthType,
  useAcceptBrandGroupInviteMutation,
  useAcceptBrandInviteMutation,
  useBrandGroupInviteQuery,
  useBrandInviteQuery,
  useLoginMutation,
} from "../../graphql/generated";
import useAnalytics from "../../hooks/useAnalytics";
import useGqlClient from "../../hooks/useGqlClient";
import { useTheme } from "../../hooks/useTheme";
import { authSelectors } from "../../store/auth/selector";
import { actions } from "../../store/auth/slice";
import styled, { css } from "../../styles";
import { TurnstileWidget } from "../unauthenticated/brands/login";

const Wrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 500px;
  margin: auto auto;
`;

const CenteredContainer = styled.div`
  flex: 1;
  display: flex;
  margin-top: -50px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: ${(p) => p.theme.spacing.xxxl};
`;

const StyledCard = styled(Card)`
  padding: ${(p) => p.theme.spacing.xl};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: ${(p) => p.theme.spacing.l};
  }
`;

const Alert = styled(View)<{ type?: "positive" | "warning" }>`
  background-color: ${(p) => p.theme.color.warning}30;
  border-left: 3px solid ${(p) => p.theme.color.warning};

  ${(p) =>
    p.type === "positive"
      ? css`
          background-color: ${(p) => p.theme.color.tags.lightGreen};
          border-left: 3px solid ${(p) => p.theme.color.tags.darkGreen}CC;
        `
      : css``}

  border-radius: 2px;
  width: 100%;
  box-sizing: border-box;

  color: ${(p) => p.theme.color.warning};
`;

const AbsoluteWrap = styled.div`
  position: absolute;
  top: 0;
  right: ${(p) => p.theme.spacing.m};
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
`;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function useHash() {
  return new URLSearchParams(useLocation().hash.substring(1));
}

// Helper to determine invite type and data
interface InviteData {
  type: "brand" | "group";
  token: string;
  email: string;
  inviterName: string;
  organizationName: string;
  organizationId: string;
}

export function Invite() {
  const query = useQuery();
  const hash = useHash();
  const client = useGqlClient();
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();
  const { track } = useAnalytics();

  // Get tokens from URL
  const oldBrandInviteToken = query.get("invite-token");
  const brandInviteToken =
    query.get("brand-invite-token") || oldBrandInviteToken;
  const groupInviteToken = query.get("brand-group-invite-token");

  // State
  const [error, setError] = useState<string | null>(null);
  const [loginMode, setLoginMode] = useState<"email" | "facebook" | "google">(
    "email"
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailPasswordErr, setEmailPasswordErr] = useState<string>("");
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const [showTurnstile, setShowTurnstile] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const isLoggedIn = useSelector(authSelectors.isLoggedIn);
  const stateHash = (+new Date().setHours(0, 0, 0, 0)).toString(36);
  const queryClient = useQueryClient();

  // Queries
  const brandInviteQuery = useBrandInviteQuery(
    client,
    { token: brandInviteToken || "" },
    { enabled: !!brandInviteToken }
  );

  const groupInviteQuery = useBrandGroupInviteQuery(
    client,
    { token: groupInviteToken || "" },
    { enabled: !!groupInviteToken }
  );

  // Mutations
  const acceptBrandInvite = useAcceptBrandInviteMutation(client);
  const acceptBrandGroupInvite = useAcceptBrandGroupInviteMutation(client);
  const loginMutation = useLoginMutation(client);

  // Derive invite data
  const inviteData: InviteData | null = React.useMemo(() => {
    if (brandInviteQuery.data) {
      const invite = brandInviteQuery.data.brandInvite;
      return {
        type: "brand",
        token: brandInviteToken!,
        email: invite.email,
        inviterName: invite.invitedBy.firstName,
        organizationName: invite.brand.name,
        organizationId: invite.brand.id,
      };
    }

    if (groupInviteQuery.data) {
      const invite = groupInviteQuery.data.brandGroupInvite;
      return {
        type: "group",
        token: groupInviteToken!,
        email: invite.email,
        inviterName: invite.invitedBy.firstName,
        organizationName: invite.brandGroup.name,
        organizationId: invite.brandGroup.id,
      };
    }

    return null;
  }, [
    brandInviteQuery.data,
    groupInviteQuery.data,
    brandInviteToken,
    groupInviteToken,
  ]);

  const isLoading = brandInviteQuery.isLoading || groupInviteQuery.isLoading;
  const hasError = brandInviteQuery.error || groupInviteQuery.error || error;

  // Reusable function to handle email/password login submission
  const handleEmailPasswordSubmit = () => {
    loginMutation.mutate(
      {
        email,
        password,
        authType: AuthType.AuthTypeBrand,
        brandInviteToken:
          inviteData?.type === "brand" ? inviteData.token : undefined,
        brandGroupInviteToken:
          inviteData?.type === "group" ? inviteData.token : undefined,
        isSignup: !isLoggedIn,
        recaptchaToken: turnstileToken,
      },
      {
        onSuccess: (data) => {
          dispatch(actions.loginSuccess({ mutation: data, type: "brand" }));
          setShouldNavigate(true);
          track(data.authenticate.signup ? "Signup success" : "Login success");

          if (inviteData && inviteData.type === "brand") {
            dispatch(
              actions.setActiveBrand({ brandId: inviteData.organizationId })
            );
          }
        },
        onError: (error) => {
          Sentry.captureException(error);
          setEmailPasswordErr("Invalid email or password");
          track("Email login failure");
          dispatch(actions.loginFailure());
          setShowTurnstile(false);
          setTurnstileToken("");
        },
      }
    );
  };

  // Effects
  useEffect(() => {
    if (shouldNavigate && isLoggedIn) {
      history.push("/confirm-details");
      setShouldNavigate(false);
    }
  }, [isLoggedIn, shouldNavigate, history]);

  // Effect to auto-submit when turnstile token is received
  useEffect(() => {
    if (turnstileToken && pendingSubmit) {
      setPendingSubmit(false);
      handleEmailPasswordSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnstileToken, pendingSubmit]);

  useEffect(() => {
    if (inviteData?.email) {
      setEmail(inviteData.email);
    }
  }, [inviteData]);

  // Handle Facebook redirect
  useEffect(() => {
    const error = query.get("error");
    if (error) {
      setError("Failed to login with Facebook, please try again");
      Sentry.captureException(
        `${query.get("error")} - ${query.get("error_reason")} - ${query.get(
          "error_description"
        )}`
      );
      return;
    }

    const state = hash.get("state");
    if (!state) return;

    const stateObj = JSON.parse(state);
    if (stateObj["hash"] !== stateHash) {
      setError("Failed to login with Facebook, please try again");
      return;
    }

    const accessToken = hash.get("access_token");
    if (!accessToken) return;

    const code = query.get("c");

    loginMutation.mutate(
      {
        fbToken: accessToken,
        code,
        authType: AuthType.AuthTypeBrand,
        brandInviteToken: stateObj.inviteToken,
        brandGroupInviteToken: stateObj.groupInviteToken,
        isSignup: !isLoggedIn,
      },
      {
        onSuccess: (data) => {
          dispatch(actions.loginSuccess({ mutation: data, type: "brand" }));
          setShouldNavigate(true);
          track(data.authenticate.signup ? "Signup success" : "Login success");

          if (inviteData && inviteData.type === "brand") {
            dispatch(
              actions.setActiveBrand({ brandId: inviteData.organizationId })
            );
          }
        },
        onError: (error) => {
          Sentry.captureException(error);
          setError(
            "Oops, that didn't work. Make sure you've selected a page AND the Instagram business account"
          );
          track("Facebook login failure");
          dispatch(actions.loginFailure());
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash.toString()]);

  // Handlers
  const handleAcceptInvite = () => {
    if (!inviteData) return;

    const mutation =
      inviteData.type === "brand" ? acceptBrandInvite : acceptBrandGroupInvite;

    if (mutation.isLoading) return;

    mutation.mutate(
      { token: inviteData.token },
      {
        onSuccess: (res: any) => {
          track("Invite accepted");

          queryClient.invalidateQueries(["Accounts"], {
            exact: false,
          });
          if (inviteData.type === "brand") {
            dispatch(
              actions.setActiveBrand({ brandId: res.acceptBrandInvite.id })
            );
          }

          // this will redirect to the correct home page
          history.push("/b");
        },
        onError: () => {
          setError("Failed to accept invite");
        },
      }
    );
  };

  const handleFacebookLogin = () => {
    if (!inviteData) return;

    setLoginMode("facebook");
    dispatch(actions.fbLoginStart());
    track("Login start");

    const stateData = {
      hash: stateHash,
      ...(inviteData.type === "brand"
        ? { inviteToken: inviteData.token }
        : { groupInviteToken: inviteData.token }),
    };

    const uri = encodeURI(
      `https://www.facebook.com/v10.0/dialog/oauth?client_id=${
        config.facebookAppId
      }&redirect_uri=${
        window.location.origin
      }/brand-invites&response_type=token&scope=public_profile&state=${JSON.stringify(
        stateData
      )}`
    );

    window.location.href = uri;
  };

  const googleLogin = useGoogleLogin({
    onSuccess: (res) => {
      setLoginMode("google");
      loginMutation.mutate(
        {
          googleToken: res.access_token,
          authType: AuthType.AuthTypeBrand,
          brandInviteToken:
            inviteData?.type === "brand" ? inviteData.token : undefined,
          brandGroupInviteToken:
            inviteData?.type === "group" ? inviteData.token : undefined,
          isSignup: !isLoggedIn,
        },
        {
          onSuccess: (data) => {
            dispatch(actions.loginSuccess({ mutation: data, type: "brand" }));
            setShouldNavigate(true);
            track(
              data.authenticate.signup ? "Signup success" : "Login success"
            );

            if (inviteData && inviteData.type === "brand") {
              dispatch(
                actions.setActiveBrand({ brandId: inviteData.organizationId })
              );
            }
          },
          onError: (error) => {
            Sentry.captureException(error);
            setError("Failed to login with Google");
            track("Google login failure");
            dispatch(actions.loginFailure());
          },
        }
      );
    },
  });

  const handleEmailLogin = async () => {
    setLoginMode("email");

    if (!email || !password) {
      setEmailPasswordErr("Please enter your email and password");
      return;
    }

    // Show Turnstile if not shown yet
    if (!showTurnstile) {
      setShowTurnstile(true);
      setPendingSubmit(true);
      return;
    }

    // If Turnstile is shown but no token yet, wait for it
    if (showTurnstile && !turnstileToken) {
      return;
    }

    // This path is only reached if user manually clicks again after turnstile
    // (shouldn't happen with auto-submit, but good as fallback)
    if (turnstileToken) {
      handleEmailPasswordSubmit();
    }
  };

  // Render error state
  if (hasError) {
    track("Invite error", {
      token: brandInviteToken || groupInviteToken || undefined,
      userHref: window.location.href,
    });

    return (
      <Page>
        <Header>
          <Logo />
        </Header>
        <CenteredContainer>
          <Wrap>
            <Text align="center" colorPreset="secondary">
              <b>Something went wrong</b>
              <br />
              Your invite may have expired
            </Text>
          </Wrap>
        </CenteredContainer>
      </Page>
    );
  }

  // Render loading state
  if (isLoading || !inviteData) {
    return (
      <Page>
        <Header>
          <Logo />
        </Header>
        <CenteredContainer>
          <Wrap>
            <Loading />
          </Wrap>
        </CenteredContainer>
      </Page>
    );
  }

  // Render main content
  const isAccepting =
    inviteData.type === "brand"
      ? acceptBrandInvite.isLoading
      : acceptBrandGroupInvite.isLoading;

  return (
    <Page>
      <Header>
        <Logo />
      </Header>
      <CenteredContainer>
        <Wrap>
          <StyledCard margin="0 0 0 0">
            <H1 margin="0">Join {inviteData.organizationName}</H1>
            <Text margin="s 0 0 0">
              <b>{inviteData.inviterName}</b> has invited you to their Joli
              account
            </Text>

            {isLoggedIn ? (
              <Button
                onClick={handleAcceptInvite}
                margin="l 0 0 0"
                disabled={isAccepting}
              >
                {isAccepting ? "Loading..." : "Accept Invite"}
              </Button>
            ) : (
              <>
                <Button
                  onClick={handleFacebookLogin}
                  style={{
                    background: "#3C72EA",
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "row",
                    justifyContent: "center",
                  }}
                  margin="l 0 l 0"
                  disabled={loginMutation.isLoading && loginMode === "facebook"}
                >
                  <Flex align="center" justify="center" margin="0 s 0 0">
                    <FacebookIconCircle />
                  </Flex>
                  {loginMutation.isLoading && loginMode === "facebook"
                    ? "Loading..."
                    : "Sign up with Facebook"}
                </Button>

                <Button
                  margin="0"
                  onClick={() => googleLogin()}
                  style={{
                    background: "#fff",
                    border: `1px solid ${theme.color.button.secondaryBorder}`,
                    color: "#445363",
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "row",
                    justifyContent: "center",
                  }}
                  disabled={loginMutation.isLoading && loginMode === "google"}
                >
                  <Flex align="center" justify="center" margin="0 s 0 0">
                    <GoogleIcon />
                  </Flex>
                  {loginMutation.isLoading && loginMode === "google"
                    ? "Loading..."
                    : "Sign up with Google"}
                </Button>

                <Flex justify="center" margin="m 0" align="center">
                  <CardDivider />
                  <Text
                    style={{
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                    }}
                    size="s"
                    colorPreset="secondary"
                    margin="0 xxl"
                  >
                    OR
                  </Text>
                  <CardDivider />
                </Flex>

                <div style={{ position: "relative" }}>
                  {inviteData.email && (
                    <AbsoluteWrap>
                      <CheckCircle checked={true} />
                    </AbsoluteWrap>
                  )}
                  <Input
                    margin="0 0 s 0"
                    isDisabled={!!inviteData.email}
                    onChange={(e) => {
                      setEmailPasswordErr("");
                      setEmail(e.currentTarget.value);
                    }}
                    value={email}
                    name="email"
                    placeholder="Email"
                  />
                </div>

                <Input
                  margin="l 0 0 0"
                  placeholder="Create a password"
                  type="password"
                  onChange={(e) => {
                    setEmailPasswordErr("");
                    setPassword(e.currentTarget.value);
                  }}
                  value={password}
                />

                <Flex justify="space-between" margin="m 0 0">
                  <Text size="s" colorPreset="secondary" margin="0">
                    Must be at least 8 characters
                  </Text>
                  <TextLink size="s" to="/forgot-password" margin="0">
                    Forgot password
                  </TextLink>
                </Flex>

                {emailPasswordErr && (
                  <Alert padding="s m" margin="l 0">
                    <Flex>
                      <Text margin="0 m 0 0">⚠️</Text>
                      <Text size="s" margin="0">
                        {emailPasswordErr}
                      </Text>
                    </Flex>
                  </Alert>
                )}

                {showTurnstile && (
                  <TurnstileWidget
                    sitekey={config.turnstileSiteKey}
                    onVerify={setTurnstileToken}
                    theme={theme.name === "dark" ? "dark" : "light"}
                  />
                )}

                <Button
                  margin="l 0 0 0"
                  onClick={handleEmailLogin}
                  disabled={loginMutation.isLoading && loginMode === "email"}
                >
                  {loginMutation.isLoading && loginMode === "email"
                    ? "Loading..."
                    : "Get Started"}
                </Button>

                <Text
                  size="xs"
                  align="center"
                  margin="l 0 0"
                  colorPreset="secondary"
                >
                  By continuing you agree to Joli's{" "}
                  <ExternalTextLink
                    size="xs"
                    href="https://joliapp.com/legal/privacy-policy"
                  >
                    Privacy Policy
                  </ExternalTextLink>{" "}
                  and{" "}
                  <ExternalTextLink
                    size="xs"
                    href="https://docs.google.com/document/d/1Pk7mgoDz2jW73MHmM0wOlrRiyUtJyuvkqZSqXpeZ_0Q/edit?usp=sharing"
                  >
                    Terms of Service
                  </ExternalTextLink>
                </Text>
              </>
            )}
          </StyledCard>

          <Text align="center">
            This invite was intended for{" "}
            <Text margin="0" isInline weight="bold">
              {inviteData.email}
            </Text>
          </Text>
        </Wrap>
      </CenteredContainer>
    </Page>
  );
}
